import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../Context/AuthContext";
import axios from "axios";
import Table from "react-bootstrap/Table";
import { Authorization } from "../../../Context/Authorization";
import Preloader from "../../../shared/PreLoaders";

// Utility constants
const LOCAL_KEY = "LoanDataCache";

// LocalStorage helpers
const saveToLocalCache = (data) => {
  localStorage.setItem(
    LOCAL_KEY,
    JSON.stringify({ ...data, timestamp: new Date().toISOString() })
  );
};

const loadFromLocalCache = () => {
  const cached = localStorage.getItem(LOCAL_KEY);
  return cached ? JSON.parse(cached) : null;
};

const formatDateTime = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
};

const LoanFirstPage = () => {
  const navigate = useNavigate();
  const { role } = useContext(AuthContext);
  const [circleData, setCircleData] = useState([]);
  const [divisionData, setDivisionData] = useState([]);
  const [rangeData, setRangeData] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedCircleId, setSelectedCircleId] = useState(null);
  const [selectedDivisionId, setSelectedDivisionId] = useState(null);
  const [selectedRangeId, setSelectedRangeId] = useState(null);
  const Api = process.env.REACT_APP_API;
  const [viewMode, setViewMode] = useState("");
  const [expandedCircles, setExpandedCircles] = useState({});
  const [expandedDivision, setExpandedDivision] = useState({});
  const [expandedRanges, setExpandedRanges] = useState({});

  const fetchCircleVfcData = async () => {
    const config = Authorization();
    try {
      const res = await axios.get(`${Api}/api/drill/loan/circle-wise`, config);
      const newData = {
        summary: res.data.summary,
        circleData: res.data.data,
        divisionData: [],
        rangeData: [],
      };
      setCircleData(newData.circleData);
      setSummary(newData.summary);
      saveToLocalCache(newData);
    } catch (error) {
      console.error("Error loading VFC data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDivisionVfcData = async () => {
    const config = Authorization();
    try {
      const res = await axios.get(`${Api}/api/drill/division-wise`, config);
      const newData = {
        summary: res.data.summary,
        divisionData: res.data.data,
        circleData: [],
        rangeData: [],
      };
      setDivisionData(newData.divisionData);
      setSummary(newData.summary);
      saveToLocalCache(newData);
    } catch (error) {
      console.error("Error loading Division VFC data:", error);
    } finally {
      setLoading(false);
    }
  };

  const rangeWiseVfcData = async () => {
    const config = Authorization();
    try {
      const res = await axios.get(`${Api}/api/drill/range-wise`, config);
      const newData = {
        summary: res.data.summary,
        rangeData: res.data.data,
        circleData: [],
        divisionData: [],
      };
      setRangeData(newData.rangeData);
      setSummary(newData.summary);
      saveToLocalCache(newData);
    } catch (error) {
      console.error("Error loading Range VFC data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDataByRole = async () => {
    if (role === "Admin") {
      setLoading(true);
      try {
        const config = Authorization();

        const [circleRes, divisionRes, rangeRes] = await Promise.all([
          axios.get(`${Api}/api/drill/loan/circle-wise`, config),
          axios.get(`${Api}/api/drill/loan/division-wise`, config),
          axios.get(`${Api}/api/drill/loan/range-wise`, config),
        ]);

        const combinedData = {
          summary: {
            ...circleRes.data.summary,
            ...divisionRes.data.summary,
            ...rangeRes.data.summary,
          },
          circleData: circleRes.data.data,
          divisionData: divisionRes.data.data,
          rangeData: rangeRes.data.data,
        };

        setSummary(combinedData.summary);
        setCircleData(combinedData.circleData);
        setDivisionData(combinedData.divisionData);
        setRangeData(combinedData.rangeData);
        saveToLocalCache(combinedData);
      } catch (error) {
        console.error("Error fetching Admin data:", error);
      } finally {
        setLoading(false);
      }
    } else if (role === "Super Admin" || role === "CCF") {
      fetchCircleVfcData();
    } else if (role === "DFO") {
      fetchDivisionVfcData();
    } else if (role === "FRO") {
      rangeWiseVfcData();
    }
  };

  useEffect(() => {
    const cached = loadFromLocalCache();
    if (cached) {
      setSummary(cached.summary);
      setCircleData(cached.circleData || []);
      setDivisionData(cached.divisionData || []);
      setRangeData(cached.rangeData || []);
      setLoading(false);
    } else {
      fetchDataByRole();
    }
  }, []);

  const shouldShowCircleCard =
    role === "Admin" || role === "Super Admin" || role === "CCF";
  const shouldShowDivisionCard = role === "Admin" || role === "DFO";
  const shouldShowRangeCard = role === "Admin" || role === "FRO";
  if (loading) return <Preloader loading={true} />;
  return (
    <div>
      <div className="page-header">
        <h3 className="page-title"> </h3>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li
              className="breadcrumb-item"
              style={{
                color: "blue",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={() => navigate(-1)}
            >
              ← Back
            </li>
            <li
              className="breadcrumb-item active"
              style={{
                color: "red",
                cursor: "pointer",
              }}
              onClick={() => navigate("/dashboard")}
            >
              🏠 Home
            </li>
            <li
              className="breadcrumb-item"
              style={{
                color: "blue",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={() => navigate(+1)}
            >
              → Previous
            </li>
          </ol>
        </nav>
      </div>

      {!loading && (
        <div className="d-flex justify-content-between align-items-center mb-3 px-2">
          <button
            className="btn btn-sm btn-outline-ocean-dusk"
            onClick={() => {
              localStorage.removeItem(LOCAL_KEY);
              setLoading(true);
              fetchDataByRole();
            }}
          >
            🔄 Refresh Now
          </button>
          <div className="text-muted small">
            Last Refreshed:{" "}
            {loadFromLocalCache()?.timestamp
              ? formatDateTime(loadFromLocalCache().timestamp)
              : "Not Available"}
          </div>
        </div>
      )}

      {/* Card Row */}
      <div className="row">
        {/* Circle Card */}
        {shouldShowCircleCard && (
          <div className="col-md-4 stretch-card grid-margin mt-3">
            <div className="card bg-gradient-CoolSky card-img-holder text-white">
              <div className="card-body">
                <img
                  src={require("../../../../assets/images/dashboard/circle.png")}
                  className="card-img-absolute"
                  alt="circle"
                />
                <h4 className="font-weight-normal mb-3">Circle</h4>
                <h3>
                  Loan's -{" "}
                  {summary.totalCircleLoanMembers.toLocaleString("en-IN") || 0}
                </h3>
              </div>
            </div>
          </div>
        )}

        {/* Division Card */}
        {shouldShowDivisionCard && (role === "DFO" || role === "Admin") && (
          <div className="col-md-4 stretch-card grid-margin mt-3">
            <div className="card bg-gradient-CoolSky card-img-holder text-white">
              <div className="card-body">
                <img
                  src={require("../../../../assets/images/dashboard/circle.png")}
                  className="card-img-absolute"
                  alt="division"
                />
                <h4 className="font-weight-normal mb-3">Division</h4>
                <h3>
                  Loan's -
                  {summary.totalDivisionLoanMembers.toLocaleString("en-IN") ||
                    0}
                </h3>
              </div>
            </div>
          </div>
        )}

        {/* Range Card */}
        {shouldShowRangeCard && (role === "FRO" || role === "Admin") && (
          <div className="col-md-4 stretch-card grid-margin mt-3">
            <div className="card bg-gradient-CoolSky card-img-holder text-white">
              <div className="card-body">
                <img
                  src={require("../../../../assets/images/dashboard/circle.png")}
                  className="card-img-absolute"
                  alt="range"
                />
                <h4 className="font-weight-normal mb-3">Range</h4>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Filter Buttons Row */}
      <div className="row mt-3">
        {shouldShowCircleCard && (
          <div className="col-md-4 mb-2">
            <button
              className={`btn btn-outline-cool-sky m-1 ${
                viewMode === "All" ? "active" : ""
              }`}
              onClick={() => {
                setViewMode("All");
                setSelectedCircleId(null);
                setExpandedCircles({});
              }}
            >
              All Circles
            </button>
          </div>
        )}

        {shouldShowDivisionCard && (
          <div className="col-md-4 mb-2">
            <button
              className={`btn btn-outline-cool-sky m-1 ${
                viewMode === "AllDivision" ? "active" : ""
              }`}
              onClick={() => {
                setViewMode("AllDivision");
                setSelectedDivisionId(null);
                setExpandedDivision({});
              }}
            >
              All Divisions
            </button>
          </div>
        )}

        {shouldShowRangeCard && (
          <div className="col-md-4 mb-2">
            <button
              className={`btn btn-outline-cool-sky m-1 ${
                selectedRangeId === "AllRanges" ? "active" : ""
              }`}
              onClick={() => {
                setViewMode("AllRanges");
                setSelectedRangeId(null);
                setExpandedRanges({});
              }}
            >
              All Ranges
            </button>
          </div>
        )}
      </div>

      {/* Show Circle Buttons only if viewMode is All or Circles */}
      {viewMode === "Circles" && (
        <div className="mt-3">
          {circleData
            .sort((a, b) => a.circleName.localeCompare(b.circleName))
            .map((circle) => (
              <button
                key={circle.circleId}
                className={`btn btn-outline-ocean-dusk m-1 ${
                  selectedCircleId === circle.circleId ? "active" : ""
                }`}
                onClick={() => {
                  setSelectedCircleId(circle.circleId);
                  if (viewMode === "Circles") {
                    // Reset all and open only this one
                    setExpandedCircles({ [circle.circleId]: true });
                  }
                }}
              >
                {circle.circleName}
              </button>
            ))}
        </div>
      )}

      {/* Tables */}
      {/* Show tables conditionally */}
      {!loading && (
        <>
          {/* All mode: show all circles */}
          {viewMode === "All" &&
            circleData
              .sort((a, b) => a.circleName.localeCompare(b.circleName))
              .map((circle) => (
                <div key={circle.circleId} className="mt-3">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      cursor: "pointer",
                      background: "white",
                      padding: "10px",
                      borderRadius: "5px",
                    }}
                    onClick={() =>
                      setExpandedCircles((prev) => ({
                        ...prev,
                        [circle.circleId]: !prev[circle.circleId],
                      }))
                    }
                  >
                    <h5 className="mb-0">{circle.circleName}</h5>
                    <span style={{ fontSize: "1.2rem" }}>
                      {expandedCircles[circle.circleId] ? "−" : "+"}
                    </span>
                  </div>

                  {expandedCircles[circle.circleId] && (
                    <Table striped bordered hover className="mt-2 text-center">
                      <thead>
                        <tr>
                          <th rowSpan="2">#</th>
                          <th rowSpan="2">Circle</th>
                          <th colSpan="3">Department Loan</th>
                          <th colSpan="3">Revolving Fund</th>
                        </tr>
                        <tr>
                          <th>Number of Male Beneficiaries</th>
                          <th>Number of Female Beneficiaries</th>
                          <th>Number of Third Gender Beneficiaries</th>
                          <th>Number of Male Beneficiaries</th>
                          <th>Number of Female Beneficiaries</th>
                          <th>Number of Third Gender Beneficiaries</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr key={circle.circleId}>
                          <td>{1}</td>
                          <td
                            style={{
                              color: "blue",
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              navigate(`/drill/circle-loans/${circle.circleId}`)
                            }
                          >
                            {circle.circleName}
                          </td>
                          {/* Department Loan Stats */}
                          <td>{circle.departmentLoan.male}</td>
                          <td>{circle.departmentLoan.female}</td>
                          <td>{circle.departmentLoan.others}</td>
                          {/* Revolving Fund Stats */}
                          <td>{circle.revolvingFundLoan.male}</td>
                          <td>{circle.revolvingFundLoan.female}</td>
                          <td>{circle.revolvingFundLoan.others}</td>
                        </tr>
                      </tbody>
                    </Table>
                  )}
                </div>
              ))}
        </>
      )}

      {!loading && (role === "DFO" || role === "Admin") && (
        <>
          {/* All mode: show AllDivisions */}
          {viewMode === "AllDivision" &&
            divisionData
              .sort((a, b) => a.divisionName.localeCompare(b.divisionName))
              .map((division, index) => (
                <div key={division.divisionId} className="mt-3">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      cursor: "pointer",
                      background: "white",
                      padding: "10px",
                      borderRadius: "5px",
                    }}
                    onClick={() =>
                      setExpandedDivision((prev) => ({
                        ...prev,
                        [division.divisionId]: !prev[division.divisionId],
                      }))
                    }
                  >
                    <h5 className="mb-0">{division.divisionName}</h5>
                    <span style={{ fontSize: "1.2rem" }}>
                      {expandedDivision[division.divisionId] ? "−" : "+"}
                    </span>
                  </div>

                  {expandedDivision[division.divisionId] && (
                    <Table striped bordered hover className="mt-2 text-center">
                      <thead>
                        <tr>
                          <th rowSpan="2">#</th>
                          <th rowSpan="2">Division</th>
                          <th colSpan="3">Department Loan</th>
                          <th colSpan="3">Revolving Fund</th>
                        </tr>
                        <tr>
                          <th>Number of Male Beneficiaries</th>
                          <th>Number of Female Beneficiaries</th>
                          <th>Number of Third Gender Beneficiaries</th>
                          <th>Number of Male Beneficiaries</th>
                          <th>Number of Female Beneficiaries</th>
                          <th>Number of Third Gender Beneficiaries</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr key={division.circleId}>
                          <td>{1}</td>
                          <td
                            style={{
                              color: "blue",
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              navigate(
                                `/drill/circle-vfc/${division.divisionId}`
                              )
                            }
                          >
                            {division.divisionName}
                          </td>
                          {/* Department Loan Stats */}
                          <td>{division.departmentLoan.male}</td>
                          <td>{division.departmentLoan.female}</td>
                          <td>{division.departmentLoan.others}</td>
                          {/* Revolving Fund Stats */}
                          <td>{division.revolvingFundLoan.male}</td>
                          <td>{division.revolvingFundLoan.female}</td>
                          <td>{division.revolvingFundLoan.others}</td>
                        </tr>
                      </tbody>
                    </Table>
                  )}
                </div>
              ))}

          {/* Division mode - selected only */}
          {viewMode === "Division" &&
            selectedDivisionId &&
            divisionData
              .filter((d) => d.divisionId === selectedDivisionId)
              .map((division, index) => (
                <div key={division.divisionId} className="mt-3">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      cursor: "pointer",
                      background: "white",
                      padding: "10px",
                      borderRadius: "5px",
                    }}
                    onClick={() =>
                      setExpandedDivision((prev) => ({
                        ...prev,
                        [division.divisionId]: !prev[division.divisionId],
                      }))
                    }
                  >
                    <h5 className="mb-0">{division.divisionName}</h5>
                    <span style={{ fontSize: "1.2rem" }}>
                      {expandedDivision[division.divisionId] ? "−" : "+"}
                    </span>
                  </div>

                  {expandedDivision[division.divisionId] && (
                    <Table striped bordered hover className="mt-2">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Circle Name</th>
                          <th>Division Name</th>
                          <th>Range</th>
                          <th>VFC</th>
                          <th className="text-center">Members</th>
                          <th className="text-center">Male</th>
                          <th className="text-center">Female</th>
                          <th className="text-center">Third Gender</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>{index + 1}</td>
                          <td>{division.circleName}</td>
                          <td
                            style={{
                              color: "blue",
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              navigate(
                                `/drill/division-vfc/${division.divisionId}`
                              )
                            }
                          >
                            {division.divisionName}
                          </td>
                          <td
                            style={{
                              color: "blue",
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              navigate(
                                `/drill/division-vfc/${division.divisionId}`
                              )
                            }
                          >
                            {division.rangeCount}
                          </td>
                          <td>{division.vfcCount}</td>
                        </tr>
                      </tbody>
                    </Table>
                  )}
                </div>
              ))}
        </>
      )}

      {!loading && viewMode === "AllRanges" && (
        <>
          {rangeData
            .sort((a, b) => a.rangeName.localeCompare(b.rangeName))
            .map((range) => (
              <div key={range.rangeId} className="mt-3">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    background: "white",
                    padding: "10px",
                    borderRadius: "5px",
                  }}
                  onClick={() =>
                    setExpandedRanges((prev) => ({
                      ...prev,
                      [range.rangeId]: !prev[range.rangeId],
                    }))
                  }
                >
                  <h5 className="mb-0">{range.rangeName}</h5>
                  <span style={{ fontSize: "1.2rem" }}>
                    {expandedRanges[range.rangeId] ? "−" : "+"}
                  </span>
                </div>

                {expandedRanges[range.rangeId] && (
                  <Table striped bordered hover className="mt-2">
                    <thead>
                      <tr>
                        <th rowSpan="2">#</th>
                        <th rowSpan="2">VFC Name</th>
                        <th colSpan="3">Department Loan</th>
                        <th colSpan="3">Revolving Fund</th>
                      </tr>
                      <tr>
                        <th>Number of Male Beneficiaries</th>
                        <th>Number of Female Beneficiaries</th>
                        <th>Number of Third Gender Beneficiaries</th>
                        <th>Number of Male Beneficiaries</th>
                        <th>Number of Female Beneficiaries</th>
                        <th>Number of Third Gender Beneficiaries</th>
                      </tr>
                    </thead>
                    <tbody>
                      {range.vfcs.map((vfc, index) => (
                        <tr key={vfc.vfcId || index}>
                          <td>{index + 1}</td>
                          <td
                            style={{
                              color: "blue",
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                            onClick={() => navigate(`/drill/vfc/${vfc.vfcId}`)}
                          >
                            {vfc.vfcName}
                          </td>
                          {/* Department Loan Stats */}
                          <td>{vfc.departmentLoan.male}</td>
                          <td>{vfc.departmentLoan.female}</td>
                          <td>{vfc.departmentLoan.others}</td>
                          {/* Revolving Fund Stats */}
                          <td>{vfc.revolvingFundLoan.male}</td>
                          <td>{vfc.revolvingFundLoan.female}</td>
                          <td>{vfc.revolvingFundLoan.others}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>
            ))}
        </>
      )}
    </div>
  );
};

export default LoanFirstPage;
